import { useMemo } from "react";
import { displayDate, useDisplayTimezone } from "@nexus/ui";
import { TriangleAlert } from "lucide-react";
import type { TimelineEvent } from "./types";
import {
  DEFAULT_TIMELINE_LABELS as DEFAULT_LABELS,
  eventLabel,
  eventSurface,
  formatClock,
  formatDayClock,
  isOngoing,
  LiveDot,
  parseDbTime,
  type EventTimelineLabels,
} from "@nexus/widget-kit";

interface EventListViewProps {
  events: TimelineEvent[];
  nowMs: number;
  selectedEventId?: string;
  interactive: boolean;
  onSelectEvent: (event: TimelineEvent) => void;
  labels?: EventTimelineLabels;
}

/**
 * The timeline answers "when"; the list answers "what", across every event in
 * scope regardless of the visible window. Same cell, same click to open the
 * detail drawer.
 */
export function EventListView({
  events,
  nowMs,
  selectedEventId,
  interactive,
  onSelectEvent,
  labels = DEFAULT_LABELS,
}: EventListViewProps) {
  const timezone = useDisplayTimezone();
  const groupedByDay = useMemo(() => {
    const groups = new Map<string, TimelineEvent[]>();

    for (const event of [...events].sort(
      (a, b) => parseDbTime(a.event_start) - parseDbTime(b.event_start),
    )) {
      const start = parseDbTime(event.event_start);
      const dayKey = displayDate(new Date(start), timezone).toISOString().slice(0, 10);
      const bucket = groups.get(dayKey);
      if (bucket) {
        bucket.push(event);
      } else {
        groups.set(dayKey, [event]);
      }
    }

    return [...groups.entries()].map(([dayKey, dayEvents]) => ({
      dayKey,
      label: formatDayClock(parseDbTime(dayEvents[0]?.event_start ?? ""), timezone),
      events: dayEvents,
    }));
  }, [events, timezone]);

  const open = (event: TimelineEvent) => () => {
    onSelectEvent(event);
  };

  return (
    <div className="min-w-0 flex-1 overflow-y-auto p-2">
      <ul className="flex min-w-0 flex-col gap-3">
        {groupedByDay.map((group) => (
          <li key={group.dayKey} className="min-w-0">
            <div className="mb-2 flex items-center gap-2 px-0.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted">
                {group.label}
              </span>
              <span className="h-px flex-1 bg-border/50" aria-hidden />
              <span className="text-xs tabular-nums text-muted">{group.events.length}</span>
            </div>

            <ul className="grid min-w-0 auto-rows-min grid-cols-1 gap-1.5 @xl/widget:grid-cols-2 @5xl/widget:grid-cols-3">
              {group.events.map((event) => {
                const start = parseDbTime(event.event_start);
                const live = isOngoing(event, nowMs);
                const selected = selectedEventId === event.event_id;
                const problem = event.unresolved_anomaly_count > 0;

                return (
                  <li key={event.event_id} className="min-w-0">
                    <button
                      type="button"
                      disabled={!interactive}
                      onClick={interactive ? open(event) : undefined}
                      aria-pressed={selected}
                      title={event.event_label}
                      className={[
                        "group flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left",
                        "transition-colors duration-100 focus-visible:outline-none focus-visible:ring-2",
                        "focus-visible:ring-inset focus-visible:ring-focus",
                        eventSurface({ selected }),
                        interactive ? "cursor-pointer" : "cursor-default",
                      ].join(" ")}
                    >
                      {live && <LiveDot tone={problem ? "error" : "accent"} />}
                      <span className={eventLabel(live || selected)}>{event.event_label}</span>
                      <span className="hidden shrink-0 text-xs tabular-nums text-muted @min-[15rem]:inline">
                        {live ? labels.live : formatClock(start, timezone)}
                      </span>
                      {problem && (
                        <span className="flex shrink-0 items-center gap-1 text-status-error-text">
                          <TriangleAlert className="h-3 w-3" aria-hidden />
                          <span className="text-xs font-semibold tabular-nums">
                            {event.unresolved_anomaly_count}
                          </span>
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}
